/* eslint-disable react/no-array-index-key */
import classNames from 'classnames';
import { useCmsClientContext } from '../../utils/cmsPageContext';
import { formatNumberWithSeparator, formatPercent } from '../../utils/helpers';
import { getClientTranslations } from '../../utils/translations/getClientTranslations';
import { CollapsibleSection } from '../CollapsibleSection/CollapsibleSection';
import { TRANSLATIONS } from './i18n';

export type SectionPremiumProps = {
  productName: string;
  isVisible: boolean;
  carInsuranceQuote?: CarInsuranceQuote;
  prefix: string;
  toggleVisibility: () => void;
};

type QuoteTableProps = {
  titleColumns: string[];
  rows: (string | number)[][];
  footer?: (string | number)[];
  productName: string;
  prefix: string;
};

export const QuoteTable: React.FC<QuoteTableProps> = ({
  titleColumns,
  rows,
  footer,
  productName,
  prefix,
}) => {
  return (
    <div className="h-fit w-full overflow-hidden rounded-lg border border-gray-300">
      <table className="w-full">
        <thead>
          <tr className="border border-b-gray-300">
            {titleColumns.map((col, index) => (
              <th
                key={`${productName}_table_col_${col}_${index}`}
                className={classNames(
                  'text-nowrap font-semibold bg-gray-100 text-gray-900 sm:px-3 sm:py-1 sm:text-sm md:px-3 md:py-2 md:text-base lg:px-4 lg:py-3 lg:text-lg',
                  {
                    'w-1/2': index === 0,
                    'w-1/4': index !== 0,
                  },
                )}
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr
              key={`${productName}_table_row_${row}_${idx}`}
              className="border border-b-gray-300"
            >
              {row.map((cell, i) => (
                <td
                  key={`${productName}_table_td_${cell}_${i}`}
                  className="font-medium text-gray-900 sm:px-3 sm:py-1 sm:text-sm md:px-3 md:py-2 md:text-base lg:px-4 lg:py-3 lg:text-lg"
                >
                  {typeof cell === 'number'
                    ? `${prefix}${formatNumberWithSeparator({ value: cell, thousandsSeparator: ',' })}`
                    : cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {footer && (
          <tfoot>
            <tr className="border border-t-gray-300 bg-white">
              {footer.map((cell, i) => (
                <td
                  key={`${productName}_table_cell_${cell}_${i}`}
                  className="px-3 py-1 font-medium text-primary-900 sm:px-3 sm:py-1 sm:text-sm md:px-3 md:py-2 md:text-base lg:px-4 lg:py-3 lg:text-lg"
                >
                  {typeof cell === 'number'
                    ? `${prefix}${formatNumberWithSeparator({ value: cell, thousandsSeparator: ',' })}`
                    : cell}
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
};

export const SectionPremiumCI: React.FC<SectionPremiumProps> = ({
  isVisible,
  carInsuranceQuote,
  toggleVisibility,
  productName,
  prefix,
}) => {
  const context = useCmsClientContext();
  const staticText = getClientTranslations(TRANSLATIONS, context);

  const formatPricePremium = (price: number): number => {
    return price % 100 === 0 ? price / 100 : price;
  };

  const calculateTax = (netPremium: number, percentage: string): number => {
    const raw = (percentage ?? '0').replace('%', '').trim();
    const perc = parseFloat(raw);
    return isNaN(perc) ? 0 : (perc / 100) * netPremium;
  };

  if (!carInsuranceQuote) return null;
  const { prices, taxes } = carInsuranceQuote;

  const priceRows = [
    [
      staticText.own_damage,
      prices.ownDamageTheft.coverage,
      prices.ownDamageTheft.price,
    ],
    [
      staticText.act_nature,
      prices.actOfNature.coverage,
      prices.actOfNature.price,
    ],
    [
      staticText.bodily_injury,
      formatPricePremium(prices.bodilyInjury.coverage),
      formatPricePremium(prices.bodilyInjury.price),
    ],
    [
      staticText.property_damage,
      formatPricePremium(prices.propertyDamage.coverage),
      formatPricePremium(prices.propertyDamage.price),
    ],
    [
      staticText.personal_accident,
      prices.personalAccident.coverage,
      prices.personalAccident.price,
    ],
  ];

  const netPremium = priceRows.reduce(
    (sum, row) => sum + (row[2] as number),
    0,
  );

  /**
   * Round each tax line up front so the displayed rows and the total are
   * derived from the same values (avoids off-by-1 rounding mismatch).
   */
  const docStampsTax = Math.round(
    calculateTax(netPremium, taxes.documentStamps.percentage),
  );
  const localGovTax = Math.round(
    calculateTax(netPremium, taxes.localGovernment.percentage),
  );
  const vatTax = Math.round(calculateTax(netPremium, taxes.vat.percentage));

  const taxRows = [
    [staticText.doc_stamps, formatPercent(taxes.documentStamps.percentage), docStampsTax],
    [staticText.local_gov, formatPercent(taxes.localGovernment.percentage), localGovTax],
    [staticText.vat, formatPercent(taxes.vat.percentage), vatTax],
  ];

  const totalTaxSum = docStampsTax + localGovTax + vatTax;

  const taxTotal = netPremium + totalTaxSum;

  return (
    <CollapsibleSection
      id="section-premium-ci"
      isOpen={isVisible}
      onToggle={toggleVisibility}
      iconName="auto_awesome"
      iconClassName="overflow-visible size-[18px] md:size-[22px] md:mb-0 md:mr-2"
      content={
        <div className="p-3 text-left">
          <div className="font-medium text-sm lg:text-base">
            {priceRows.length > 0 && (
              <div className="space-y-6">
                <QuoteTable
                  titleColumns={[
                    staticText.type,
                    staticText.coverage,
                    staticText.price,
                  ]}
                  rows={priceRows}
                  footer={[staticText.net_premium, '', netPremium]}
                  productName={productName}
                  prefix={prefix}
                />
                <QuoteTable
                  titleColumns={[
                    staticText.tax,
                    staticText.tax_rate,
                    staticText.price,
                  ]}
                  rows={taxRows}
                  footer={[staticText.taxes_total, '', taxTotal]}
                  productName={productName}
                  prefix={prefix}
                />
              </div>
            )}
          </div>
        </div>
      }
    >
      <span className="mhg-alpha-bullet-2 lg:mhg-alpha-bullet-1">
        {staticText.premium}
      </span>
    </CollapsibleSection>
  );
};

// TODO remove, when integrate cmsServiceClient
export type CarInsuranceQuote = {
  productId: string;
  productName: string;
  providerId: string;
  providerName: string;
  netPremium: number;
  totalPremium: number;
  deductible: number;
  prices: CarInsuranceQuotePrices;
  taxes: CarInsuranceQuoteTaxes;
  fmv: number;
};

export type CarInsuranceQuoteTaxes = {
  documentStamps: CarInsuranceQuoteTax;
  localGovernment: CarInsuranceQuoteTax;
  vat: CarInsuranceQuoteTax;
};

export type CarInsuranceQuotePrices = {
  ownDamageTheft: CarInsuranceQuoteCoveragePrice;
  actOfNature: CarInsuranceQuoteCoveragePrice;
  bodilyInjury: CarInsuranceQuoteCoveragePrice;
  propertyDamage: CarInsuranceQuoteCoveragePrice;
  personalAccident: CarInsuranceQuoteCoveragePrice;
};

export type CarInsuranceQuoteCoveragePrice = {
  coverage: number;
  price: number;
};

export type CarInsuranceQuoteTax = {
  percentage: string;
  price: number;
};
